import range from "lodash/range";
import sortBy from "lodash/sortBy";
import { combineReducers } from "redux";
import { createSelector } from "reselect";
import { getType } from "typesafe-actions";
import { ISelectOption, RootAction, RootState } from "../";
import * as bootstrapActions from "../bootstrap/actions";
import { getElementStats } from "../element-stats/reducers";
import { getElementTypes } from "../element-types/reducers";
import * as elementActions from "../elements/actions";
import { getLastEvent, getNextEvent } from "../events/reducers";
import { getOverrides } from "../overrides/reducers";
import { getWatched } from "../player/reducers";
import { getTeams } from "../teams/reducers";
import {
  IElement,
  IElementFixture,
  IElementFixtureBlank,
  IState,
} from "./types";

export const formatNameForSearching = (name: string): string => {
  const search = name.toLowerCase();
  const from = "ąàáäâãåæăćčĉęèéëêĝĥìíïîĵłľńňòóöőôõðøśșşšŝťțţŭùúüűûñÿýçżźž.";
  const to = "aaaaaaaaaccceeeeeghiiiijllnnoooooooossssstttuuuuuunyyczzz ";
  return search.replace(/.{1}/g, (c) => {
    const index = from.indexOf(c);
    return index === -1 ? c : to[index];
  });
};

// State Reducer
export default combineReducers<IState, RootAction>({
  byId: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(bootstrapActions.addElements): {
        const newState: { [key: string]: IElement } = {};
        action.payload.forEach((element) => {
          newState[element.id] = {
            ...element,
            search_name: formatNameForSearching(element.web_name),
          };
        });
        return newState;
      }
      default:
        return state;
    }
  },

  controls: (
    state = {
      filter: "",
      search: "",
      sort: "total_points",
      maxCost: 0,
      getUnavailable: true,
    },
    action: RootAction,
  ) => {
    switch (action.type) {
      case getType(elementActions.updateElementControls):
        return action.payload;
      default:
        return state;
    }
  },

  dialog: (state = 0, action: RootAction) => {
    switch (action.type) {
      case getType(elementActions.elementDialogShow):
        return action.payload;
      case getType(elementActions.elementDialogHide):
        return 0;
      default:
        return state;
    }
  },

  fixturesById: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(elementActions.fetchElementSummary.success):
        return {
          ...state,
          [action.payload.id]: action.payload.data.fixtures,
        };
      default:
        return state;
    }
  },

  eventDataById: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(elementActions.fetchElementEventData.success):
        return {
          ...state,
          [action.payload.id]: action.payload.data.elements.reduce(
            (memo, data) => ({
              ...memo,
              [data.id]: {
                explain: data.explain,
                stats: data.stats,
              },
            }),
            {},
          ),
        };
      default:
        return state;
    }
  },

  historyById: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(elementActions.fetchElementSummary.success):
        return {
          ...state,
          [action.payload.id]: action.payload.data.history,
        };
      default:
        return state;
    }
  },

  seasonHistoryById: (state = {}, action: RootAction) => {
    switch (action.type) {
      case getType(elementActions.fetchElementSummary.success):
        return {
          ...state,
          [action.payload.id]: action.payload.data.history_past,
        };
      default:
        return state;
    }
  },
});

// State Selectors and helper functions
export const getElementsById = (
  state: RootState,
  eventId?: number,
  chipId?: number,
) => {
  const overrides = getOverrides(state, eventId, chipId);
  if (!overrides) {
    return state.elements.byId;
  }
  return {
    ...state.elements.byId,
    ...overrides.elements?.byId,
  };
};

export const getElement = createSelector(
  [
    (state: RootState, _: any, eventId?: number) =>
      getElementsById(state, eventId, undefined),
    (_: any, elementId: number) => elementId,
  ],
  (elementsById, elementId) => {
    return elementsById[elementId];
  },
);

export const getElements = createSelector(
  [(state: RootState, eventId?: number) => getElementsById(state, eventId)],
  (elements) => {
    return Object.keys(elements).map((e) => elements[e]);
  },
);

export const getElementControls = (state: RootState) => state.elements.controls;

export const getElementCount = createSelector(
  getElements,
  (elements) => elements.length,
);

export const getElementTypeFilterOptions = createSelector(
  getElementTypes,
  (types): ISelectOption[] =>
    types.map((et) => ({
      label: et.plural_name,
      value: `et_${et.id}`,
    })),
);

export const getTeamFilterOptions = createSelector(
  getTeams,
  (teams): (ISelectOption & { id: number })[] =>
    teams.map((t) => ({
      label: t.name,
      value: `te_${t.id}`,
      id: t.id,
    })),
);

export const getFilterOptions = createSelector(
  getElementTypeFilterOptions,
  getTeamFilterOptions,
  (types, teams) => [...types, ...teams],
);

export const getFilter = (filter: string) => {
  const filters = {
    element_type: 0,
    team: 0,
  };
  const filterRegexp = /^(te|et)_(\d+)$/;
  const matches = filter.match(filterRegexp);
  if (matches) {
    const key = matches[1];
    const value = parseInt(matches[2], 10);
    switch (key) {
      case "te":
        filters.team = value;
        break;
      case "et":
        filters.element_type = value;
        break;
      default:
        break;
    }
  }
  return filters;
};

export const getSafeSearchRegexp = (search: string) => {
  const safeValue = formatNameForSearching(search).replace(
    /[-/\\^$*+?.()|[\]{}]/g,
    "\\$&",
  );
  return new RegExp(`(^${safeValue}| ${safeValue})`);
};

export const filterElements = (
  elements: IElement[],
  filterString: string,
  watched: number[],
) => {
  if (filterString === "wl") {
    return watchedElements(elements, watched);
  }
  const filter = getFilter(filterString);
  return elements.filter((e) => {
    if (!filterString) {
      return false;
    }
    if (filter.team && filter.team !== e.team) {
      return false;
    }
    if (filter.element_type && filter.element_type !== e.element_type) {
      return false;
    }
    return true;
  });
};

export const watchedElements = (elements: IElement[], watched: number[]) =>
  elements.filter((e) => watched.indexOf(e.code) > -1);

export const maxCostFromElements = (elements: IElement[]) =>
  elements.length
    ? elements.reduce(
        (max, e) => (e.now_cost > max ? e.now_cost : max),
        elements[0].now_cost,
      )
    : 0;

export const minCostFromElements = (elements: IElement[]) =>
  elements.length
    ? elements.reduce(
        (min, e) => (e.now_cost < min ? e.now_cost : min),
        elements[0].now_cost,
      )
    : 0;

export const reduceElements = (
  elements: IElement[],
  search: string,
  maxCost: number,
  toSpend?: number,
) => {
  const searchRegexp = getSafeSearchRegexp(search);
  const maxCostIsAffordable = maxCost === toSpend;
  return elements.filter((e) => {
    if (search && !e.search_name.match(searchRegexp)) {
      return false;
    }
    if (toSpend != null) {
      if (maxCostIsAffordable && e.now_cost > toSpend) {
        return false;
      }
    }
    if (maxCost && e.now_cost > maxCost) {
      return false;
    }
    return true;
  });
};

const ascendingSort: Array<keyof IElement> = ["chance_of_playing_next_round"];

const stringSort: Array<keyof IElement> = ["news_added"];

export const sortElements = (elements: IElement[], sort: keyof IElement) =>
  elements.sort((a, b) => {
    let aSort, bSort;
    if (stringSort.indexOf(sort) > -1) {
      aSort = a[sort] || "";
      bSort = b[sort] || "";
    } else {
      // sort value must be either a float represented as a string or a number
      // all bets are off if not ...
      aSort = a[sort] || 0;
      bSort = b[sort] || 0;
      if (typeof aSort === "string" && typeof bSort === "string") {
        aSort = parseFloat(aSort);
        bSort = parseFloat(bSort);
      }
    }

    if (ascendingSort.indexOf(sort) > -1) {
      bSort = [aSort, (aSort = bSort)][0];
    }

    // Descending stat order
    if (aSort !== bSort) {
      return aSort > bSort ? -1 : 1;
    }

    // Ascending team order
    if (a.team !== b.team) {
      return a.team > b.team ? 1 : -1;
    }

    // Ascending element type order
    if (a.element_type !== b.element_type) {
      return a.element_type > b.element_type ? 1 : -1;
    }

    // Finally ascending id
    return a.id > b.id ? 1 : -1;
  });

export const getElementsFromControls = createSelector(
  getElements,
  getElementControls,
  getWatched,
  (elements, controls, watched) => {
    const filteredElements = filterElements(
      elements.filter((e) => controls.getUnavailable || e.status !== "u"),
      controls.filter,
      watched,
    );

    return {
      data: sortElements(
        reduceElements(filteredElements, controls.search, controls.maxCost),
        controls.sort,
      ),
      maxCost: maxCostFromElements(filteredElements),
      minCost: minCostFromElements(filteredElements),
    };
  },
);

export const getAverageCostByType = createSelector(
  getElements,
  (
    elements,
  ): {
    [key: string]: number;
  } => {
    const elCosts: {
      [key: string]: {
        total: number;
        count: number;
      };
    } = {};
    elements.forEach((e) => {
      if (!elCosts[e.element_type]) {
        elCosts[e.element_type] = { total: 0, count: 0 };
      }
      elCosts[e.element_type].total += e.now_cost;
      elCosts[e.element_type].count++;
    });
    return Object.keys(elCosts).reduce(
      (memo, et) => ({
        ...memo,
        [et]: elCosts[et].total / elCosts[et].count,
      }),
      {},
    );
  },
);

export const getElementDialog = (state: RootState) => state.elements.dialog;

export const getFixturesById = (state: RootState) =>
  state.elements.fixturesById;

export const getFixtures = (state: RootState, elementId: number) => {
  const fixturesById = getFixturesById(state);
  return fixturesById[elementId] || [];
};

export const getFixturesWithBlanks = (state: RootState, elementId: number) => {
  const fixtures = getFixtures(state, elementId);
  const nxt = getNextEvent(state);
  const lst = getLastEvent(state);

  // No more events so just return any fixtures
  if (!nxt) {
    return fixtures;
  }

  // Pad out any blank events
  const fixturesWithBlanks: Array<IElementFixture | IElementFixtureBlank> = [];
  let expectedNextEventId = nxt.id;
  fixtures.forEach((f) => {
    // TBA fixtures are at the end
    if (!f.event) {
      fixturesWithBlanks.push(f);
      return;
    }
    if (f.event > expectedNextEventId) {
      range(expectedNextEventId, f.event).map((i) =>
        fixturesWithBlanks.push({ code: null, event: i }),
      );
    }
    fixturesWithBlanks.push(f);
    expectedNextEventId = f.event + 1;
  });

  // Also need to consider missing fixture until the last event
  if (lst && lst.id >= expectedNextEventId) {
    range(expectedNextEventId, lst.id + 1).map((i) =>
      fixturesWithBlanks.push({ code: null, event: i }),
    );
  }

  return fixturesWithBlanks;
};

export const getHistoryById = (state: RootState) => state.elements.historyById;

export const getHistory = (state: RootState, elementId: number) => {
  const historyById = getHistoryById(state);
  return historyById[elementId] || [];
};

export const getHistoryTotals = (state: RootState, elementId: number) => {
  const statNames = [
    ...getElementStats(state).map((s) => s.name),
    "total_points",
  ];
  const totals: Record<string, number | string> = statNames.reduce(
    (memo, n) => ({ ...memo, [n]: 0 }),
    {},
  );
  getHistory(state, elementId).forEach((h) => {
    statNames.forEach((s) => {
      totals[s] = Number(totals[s]) + Number(h[s]);
    });
  });
  statNames.forEach((s) => {
    if (!Number.isInteger(totals[s])) {
      totals[s] = Number(totals[s]).toFixed(1);
    }
  });
  return totals;
};

export const getSeasonHistoryById = (state: RootState) =>
  state.elements.seasonHistoryById;

export const getSeasonHistory = (state: RootState, elementId: number) => {
  const seasonHistoryById = getSeasonHistoryById(state);
  return seasonHistoryById[elementId] || [];
};

export const getElementsEventDataById = (state: RootState, eventId: number) =>
  state.elements.eventDataById[eventId] || null;

export const getTotalsByType = (elements: IElement[]) =>
  sortBy(elements, "element_type").reduce<Record<string, number>>(
    (memo, e) => ({
      ...memo,
      [e.element_type]: memo[e.element_type] ? memo[e.element_type] + 1 : 1,
    }),
    {},
  );

export const getFormation = (elements: IElement[]) => {
  const totals = getTotalsByType(elements);
  return sortBy(Object.keys(totals), (et) => Number(et))
    .map((et) => totals[et])
    .join("-");
};
